// src/versions/versionRegistry.ts

export interface PageMetadata {
  name: string;
  path: string;
  imgKey: string; // Optional for backward compatibility
  availableIn: string[];
  deprecated?: string[];
}

export interface ComponentMetadata {
  name: string;
  path: string;
  imgKey: string; // Optional for backward compatibility
  availableIn: string[];
  deprecated?: string[];
}

export interface VersionConfig {
  id: string;
  label: string;
  internalId: "v1" | "v2" | "v3";
  muiVersion: string;
  description: string;
  components: ComponentMetadata[];
  introductions: PageMetadata[];
  foundations: PageMetadata[];
  availablePages: string[];
  pages:PageMetadata[];
}

export const VERSIONS: Record<string, VersionConfig> = {
  'v1.1.0': {
    id: 'v1.1.0',
    label: 'v1.0.0',
    internalId: 'v1',
    muiVersion: '^5.10.0',
    description: 'Initial release',
    pages:[
      { name: 'Introductions', path: 'introductions', imgKey: 'introductions', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Foundations', path: 'foundations', imgKey: 'foundations', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Icons', path: 'icons', imgKey: 'icons', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Components', path: 'components', imgKey: 'components', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
    ],
    components: [
      { name: 'Accordion', path: 'muiaccordion', imgKey: 'accordion', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Alerts', path: 'muialerts', imgKey: 'alerts', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Autocomplete', path: 'muiautocomplete', imgKey: 'autocomplete', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Avatar', path: 'muiavatar', imgKey: 'avatar', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Badge', path: 'muibadge', imgKey: 'badges', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Box', path: 'muibox', imgKey: 'box', availableIn: ['v1.1.0', 'v1.2.0'], deprecated: ['v1.2.0'] },
      { name: 'Breadcrumbs', path: 'muibreadcrumbs', imgKey: 'breadcrumbs', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Button', path: 'muibuttons', imgKey: 'button', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Button Group', path: 'muibuttongroup', imgKey: 'buttongroup', availableIn: ['v1.1.0', 'v1.2.0'], deprecated: ['v1.2.0'] },
      { name: 'Card', path: 'card', imgKey: 'card', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Radio / Checkbox', path: 'muicheckbox', imgKey: 'checkbox', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Data Grid', path: 'muidatatable', imgKey: 'datatable', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Date Picker', path: 'muidatepicker', imgKey: 'datepicker', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Time Picker', path: 'muitimepicker', imgKey: 'datepicker', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Dialogs', path: 'muidialogs', imgKey: 'dialog', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Drawer', path: 'muisidebar', imgKey: 'sidebar', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Form', path: 'muiform', imgKey: 'form', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Grid', path: 'grid', imgKey: 'grid', availableIn: ['v1.1.0'], deprecated: ['v1.2.0'] },
      { name: 'Link', path: 'muilink', imgKey: 'muilink', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'List', path: 'muilist', imgKey: 'list', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Menu', path: 'muimenu', imgKey: 'menu', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Popover', path: 'muipopover', imgKey: 'popover', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Side Navigation', path: 'muisidenavigation', imgKey: 'sidenavigation', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Stepper', path: 'muistepper', imgKey: 'stepper', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Switch', path: 'muiswitch', imgKey: 'switch', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Tabs', path: 'muitabs', imgKey: 'tabs', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Textarea', path: 'muitextareaautosize', imgKey: 'textarea', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Toaster', path: 'muitoaster', imgKey: 'toaster', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Tooltip', path: 'muitooltip', imgKey: 'tooltip', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Transfer List', path: 'muitransferlist', imgKey: 'transferlist', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Tree View', path: 'muitreeviewlist', imgKey: 'treeview', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Typography', path: 'muitypography', imgKey: 'typography', availableIn: ['v1.1.0', 'v1.2.0'] },
    ],

    introductions: [
      { name: 'Getting Started', path: 'gettingstarted', imgKey: 'gettingstarted', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'What is new', path: 'whatisnew', imgKey: 'whatisnew', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Browser Support', path: 'browsersupport', imgKey: 'browsersupport', availableIn: ['v1.2.0'] },
    ],

    foundations: [
      { name: 'Colors', path: 'colors', imgKey: 'colors', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Logos', path: 'logos', imgKey: 'logos', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Spacing', path: 'spacing', imgKey: 'spacing', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Typography', path: 'typographies', imgKey: 'typography', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Layout', path: 'layout', imgKey: 'layout', availableIn: ['v1.1.0'], deprecated: ['v1.2.0'] },
    ],
    
    availablePages: ['pages', 'components', 'foundations', 'introductions'],
  },

  'v1.2.0': {
    id: 'v1.2.0',
    label: 'v2.3.1',
    internalId: 'v2',
    muiVersion: '^5.14.0',
    description: 'Enhanced version with Grid v2',
     pages:[
      { name: 'Introductions', path: 'introductions', imgKey: 'introductions', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Foundations', path: 'foundations', imgKey: 'foundations', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Icons', path: 'icons', imgKey: 'icons', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Components', path: 'components', imgKey: 'components', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
    ],
    components: [
      { name: 'Accordion', path: 'muiaccordion', imgKey: 'accordion', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Alerts', path: 'muialerts', imgKey: 'alerts', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Autocomplete', path: 'muiautocomplete', imgKey: 'autocomplete', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Avatar', path: 'muiavatar', imgKey: 'avatar', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Badge', path: 'muibadge', imgKey: 'badges', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Box', path: 'muibox', imgKey: 'box', availableIn: ['v1.1.0', 'v1.2.0'], deprecated: ['v1.2.0'] },
      { name: 'Breadcrumbs', path: 'muibreadcrumbs', imgKey: 'breadcrumbs', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Button', path: 'muibuttons', imgKey: 'button', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Button Group', path: 'muibuttongroup', imgKey: 'buttongroup', availableIn: ['v1.1.0', 'v1.2.0'], deprecated: ['v1.2.0'] },
      { name: 'Card', path: 'muicard', imgKey: 'card', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Radio / Checkbox', path: 'muicheckbox', imgKey: 'checkbox', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Data Grid', path: 'muidatatable', imgKey: 'datatable', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Date Picker', path: 'muidatepicker', imgKey: 'datepicker', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Time Picker', path: 'muitimepicker', imgKey: 'datepicker', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Dialogs', path: 'muidialogs', imgKey: 'dialog', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Drawer', path: 'muisidebar', imgKey: 'sidebar', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Form', path: 'muiform', imgKey: 'form', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Grid', path: 'grid', imgKey: 'grid', availableIn: ['v1.1.0', 'v1.2.0'], deprecated: ['v1.2.0'] },
      { name: 'Grid v2', path: 'gridv2', imgKey: 'grid', availableIn: ['v1.2.0'] },
      { name: 'Link', path: 'muilink', imgKey: 'muilink', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'List', path: 'muilist', imgKey: 'list', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Menu', path: 'muimenu', imgKey: 'menu', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Popover', path: 'muipopover', imgKey: 'popover', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Side Navigation', path: 'muisidenavigation', imgKey: 'sidenavigation', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Stepper', path: 'muistepper', imgKey: 'stepper', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Switch', path: 'muiswitch', imgKey: 'switch', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Tabs', path: 'muitabs', imgKey: 'tabs', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Textarea', path: 'muitextareaautosize', imgKey: 'textarea', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Toaster', path: 'muitoaster', imgKey: 'toaster', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Tooltip', path: 'muitooltip', imgKey: 'tooltip', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Transfer List', path: 'muitransferlist', imgKey: 'transferlist', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Tree View', path: 'muitreeviewlist', imgKey: 'treeview', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Typography', path: 'muitypography', imgKey: 'typography', availableIn: ['v1.1.0', 'v1.2.0'] },
    ],

    introductions: [
      { name: 'Getting Started', path: 'gettingstarted', imgKey: 'gettingstarted', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'What is new', path: 'whatisnew', imgKey: 'whatisnew', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Browser Support', path: 'browsersupport', imgKey: 'browsersupport', availableIn: ['v1.2.0','v3.0.0'] },
    ],

    foundations: [
      { name: 'Colors', path: 'colors', imgKey: 'colors', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Logos', path: 'logos', imgKey: 'logos', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Spacing', path: 'spacing', imgKey: 'spacing', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Typography', path: 'typographies', imgKey: 'typography', availableIn: ['v1.1.0', 'v1.2.0'] },
      { name: 'Layout', path: 'layout', imgKey: 'layout', availableIn: ['v1.1.0', 'v1.2.0'] },
    ],
    
    availablePages: ['pages', 'components', 'foundations', 'introductions'],
  },
  'v3.0.0': {
    id: 'v3.0.0',
    label: 'v3.0.0',
    internalId: 'v3',
    muiVersion: '^5.14.0',
    description: 'Enhanced version with Grid v2',

    pages:[
      { name: 'Introductions', path: 'introductions', imgKey: 'introductions', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Foundations', path: 'foundations', imgKey: 'foundations', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Icons', path: 'icons', imgKey: 'icons', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Components', path: 'components', imgKey: 'components', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
    ],
    
    components: [
      { name: 'Accordion', path: 'muiaccordion', imgKey: 'accordion', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Alerts', path: 'muialerts', imgKey: 'alerts', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Autocomplete', path: 'muiautocomplete', imgKey: 'autocomplete', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Avatar', path: 'muiavatar', imgKey: 'avatar', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Badge', path: 'muibadge', imgKey: 'badges', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Box', path: 'muibox', imgKey: 'box', availableIn: ['v1.1.0', 'v1.2.0'], deprecated: ['v1.2.0'] },
      { name: 'Breadcrumbs', path: 'muibreadcrumbs', imgKey: 'breadcrumbs', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Button', path: 'muibuttons', imgKey: 'button', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Button Group', path: 'muibuttongroup', imgKey: 'buttongroup', availableIn: ['v1.1.0', 'v1.2.0'], deprecated: ['v1.2.0'] },
      { name: 'Card', path: 'muicard', imgKey: 'card', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Radio / Checkbox', path: 'muicheckbox', imgKey: 'checkbox', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Data Grid', path: 'muidatatable', imgKey: 'datatable', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Date Picker', path: 'muidatepicker', imgKey: 'datepicker', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Time Picker', path: 'muitimepicker', imgKey: 'datepicker', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Dialogs', path: 'muidialogs', imgKey: 'dialog', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Drawer', path: 'muisidebar', imgKey: 'sidebar', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Form', path: 'muiform', imgKey: 'form', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Grid', path: 'grid', imgKey: 'grid', availableIn: ['v1.1.0', 'v1.2.0'], deprecated: ['v1.2.0'] },
      { name: 'Grid v2', path: 'gridv2', imgKey: 'grid', availableIn: ['v1.2.0','v3.0.0','v3.0.0'] },
      { name: 'Link', path: 'muilink', imgKey: 'muilink', availableIn: ['v1.1.0', 'v1.2.0'], },
      { name: 'List', path: 'muilist', imgKey: 'list', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Menu', path: 'muimenu', imgKey: 'menu', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Popover', path: 'muipopover', imgKey: 'popover', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Side Navigation', path: 'muisidenavigation', imgKey: 'sidenavigation', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Stepper', path: 'muistepper', imgKey: 'stepper', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Switch', path: 'muiswitch', imgKey: 'switch', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Skeleton', path: 'Skeleton', imgKey: 'Skeleton', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Tabs', path: 'muitabs', imgKey: 'tabs', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Toggle Button', path: 'togglebutton', imgKey: 'togglebutton', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Textarea', path: 'muitextareaautosize', imgKey: 'textarea', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Toaster', path: 'muitoaster', imgKey: 'toaster', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0', 'v3.0.0'] },
      { name: 'Tooltip', path: 'muitooltip', imgKey: 'tooltip', availableIn: ['v1.1.0', 'v1.2.0','v3.0.0'] },
      { name: 'Transfer List', path: 'muitransferlist', imgKey: 'transferlist', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Tree View', path: 'muitreeviewlist', imgKey: 'treeview', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Typography', path: 'muitypography', imgKey: 'typography', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
    ],

    introductions: [
      { name: 'Getting Started', path: 'gettingstarted', imgKey: 'gettingstarted', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'What is new', path: 'whatisnew', imgKey: 'whatisnew', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Browser Support', path: 'browsersupport', imgKey: 'browsersupport', availableIn: ['v1.2.0', 'v3.0.0'] },
    ],

    foundations: [
      { name: 'Colors', path: 'colors', imgKey: 'colors', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Logos', path: 'logos', imgKey: 'logos', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Spacing', path: 'spacing', imgKey: 'spacing', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Typography', path: 'typographies', imgKey: 'typography', availableIn: ['v1.1.0', 'v1.2.0', 'v3.0.0'] },
      { name: 'Layout', path: 'layout', imgKey: 'layout', availableIn: ['v1.1.0','v1.2.0', 'v3.0.0'] },
    ],
    
    availablePages: ['pages','icons','components', 'foundations', 'introductions'],
  },
};

// Core helper functions
export const getVersions = () => Object.values(VERSIONS);
export const getVersion = (id: string) => VERSIONS[id];
export const getVersionByInternalId = (internalId: 'v1' | 'v2' | 'v3') => 
  Object.values(VERSIONS).find(v => v.internalId === internalId);

export const getAvailableIntroductions = (versionId: string) => {
  const version = getVersion(versionId);
  return version?.introductions.filter(intro => intro.availableIn.includes(versionId)) || [];
};

export const getAvailableFoundations = (versionId: string) => {
  const version = getVersion(versionId);
  return version?.foundations.filter(foundation => foundation.availableIn.includes(versionId)) || [];
};

export const getAvailableComponents = (versionId: string) => {
  const version = getVersion(versionId);
  return version?.components.filter(comp => comp.availableIn.includes(versionId)) || [];
};
export const getAvailablePages = (versionId: string) => {
  const version = getVersion(versionId);
  return version?.components.filter(page => page.availableIn.includes(versionId)) || [];
};
// ✅ FIXED: Complete deprecated check for all types
export const isPageDeprecated = (versionId: string, pagePath: string, type: 'intro' | 'foundation' | 'component') => {
  const version = getVersion(versionId);
  let pages;
  
  switch (type) {
    case 'intro':
      pages = version?.introductions;
      break;
    case 'foundation':
      pages = version?.foundations;
      break;
    case 'component':  // ✅ This was missing!
      pages = version?.components;
      break;
    default:
      return false;
  }
  
  const page = pages?.find(p => p.path === pagePath);
  return page?.deprecated?.includes(versionId) ?? false;
};
